import { useState, useCallback } from 'react';
import { useGovernance } from '../hooks/useGovernance';
import { useVoting } from '../hooks/useVoting';
import { useVotingPower } from '../hooks/useVotingPower';
import { useSearch } from '../hooks/useSearch';
import { KanbanBoard } from './KanbanBoard';
import { CategoryFilter } from './CategoryFilter';
import { VotingPowerIndicator } from './VotingPowerIndicator';
import { ProposalModal } from './ProposalModal';
import { ToastContainer } from './ToastContainer';
import { ThemeToggle } from './ThemeToggle';
import { WalletConnectButton } from './WalletConnectButton';
import { SearchBar } from './SearchBar';
import { StatisticsDashboard } from './StatisticsDashboard';
import { NotificationPreferences } from './NotificationPreferences';

/**
 * Main Governance Dashboard Component
 * Displays Kanban-style proposal board with voting
 */
export function GovernanceDashboard({ config }) {
  const [selectedCategory, setSelectedCategory] = useState(config.category || '');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [showStatistics, setShowStatistics] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  // Hooks
  const search = useSearch();
  const { proposalsByStatus, loading, error, refetch } = useGovernance(selectedCategory, search.getQueryParams());
  const voting = useVoting();
  const votingPower = useVotingPower(selectedCategory);

  // Category-specific column definitions (matches PHP T24_Status_Validator)
  const categoryColumns = {
    'design': ['in-creation', 'published', 'completed', 'cancelled'],
    'development': ['in-development', 'in-testing', 'in-qa', 'planned-next-version', 'published', 'completed', 'cancelled'],
    'marketing': ['in-creation', 'planned', 'published', 'completed', 'cancelled'],
    'finance': ['budget-review', 'pending-approval', 'approved', 'in-execution', 'completed', 'rejected'],
    'steering': ['proposal-draft', 'community-discussion', 'voting', 'approved', 'in-implementation', 'completed', 'rejected']
  };

  // Get columns to display based on selected category
  const displayColumns = selectedCategory && categoryColumns[selectedCategory]
    ? categoryColumns[selectedCategory]
    : (config.columns?.split(',') || ['in-creation', 'in-development', 'planned', 'budget-review', 'proposal-draft', 'published', 'approved', 'voting', 'completed', 'cancelled', 'rejected']);

  // Toast notification system
  const showToast = useCallback((message, type = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type }]);

    // Auto-remove after 4 seconds
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  // Handle vote submission
  const handleVote = useCallback(
    async (proposalId, voteType) => {
      try {
        await voting.submitVote(proposalId, voteType);
        showToast(window.t24Governance.strings.voteSuccess, 'success');

        // Refetch proposals to get updated vote counts
        setTimeout(() => refetch(), 500);
      } catch (err) {
        showToast(err.message || window.t24Governance.strings.voteError, 'error');
      }
    },
    [voting, refetch, showToast]
  );

  // Handle proposal card click
  const handleProposalClick = useCallback((proposal) => {
    setSelectedProposal(proposal);
  }, []);

  // Close proposal modal
  const handleCloseModal = useCallback(() => {
    setSelectedProposal(null);
  }, []);

  // Handle category change
  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Render loading state
  if (loading && !proposalsByStatus) {
    return (
      <div className="t24-governance-loading">
        <div className="t24-loading-spinner"></div>
        <p className="t24-loading-text">
          {window.t24Governance.strings.loadingProposals}
        </p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="t24-governance-error">
        <p>Error loading proposals: {error}</p>
        <button onClick={refetch} className="t24-btn-retry">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="t24-governance-dashboard">
      {/* Header */}
      <div className="t24-governance-header">
        <h1 className="t24-governance-title">Governance Roadmap</h1>

        <div className="t24-governance-header-actions">
          {/* Statistics Button */}
          <button
            className="t24-btn-icon"
            onClick={() => setShowStatistics(true)}
            aria-label="View Statistics"
            title="View Statistics"
          >
            📊
          </button>

          {/* Notification Preferences Button (logged in users only) */}
          {window.t24Governance.isLoggedIn && (
            <button
              className="t24-btn-icon"
              onClick={() => setShowNotifications(true)}
              aria-label="Notification Settings"
              title="Notification Settings"
            >
              ⚙️
            </button>
          )}

          {/* Theme Toggle */}
          <ThemeToggle />

          {/* Wallet Connect Button */}
          <WalletConnectButton onShowToast={showToast} />

          {/* Voting Power Indicator (only if logged in with role) */}
          {window.t24Governance.isLoggedIn && votingPower.votingPower > 0 && (
            <VotingPowerIndicator
              votingPower={votingPower.votingPower}
              loading={votingPower.loading}
              roleLabel={votingPower.getRoleTypeLabel()}
            />
          )}
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        searchTerm={search.searchTerm}
        filters={search.filters}
        onSearchChange={search.updateSearch}
        onFilterChange={search.updateFilter}
        onSortChange={search.updateSort}
      />

      {/* Category Filter */}
      <CategoryFilter
        categories={window.t24Governance.categories}
        selectedCategory={selectedCategory}
        onCategoryChange={handleCategoryChange}
      />

      {/* Kanban Board */}
      <KanbanBoard
        proposalsByStatus={proposalsByStatus}
        statuses={window.t24Governance.statuses}
        columns={displayColumns}
        onProposalClick={handleProposalClick}
        onVote={handleVote}
        voting={voting}
        showVoting={config.showVoting !== 'false'}
        userVotes={voting.userVotes}
      />

      {/* Proposal Detail Modal */}
      {selectedProposal && (
        <ProposalModal
          proposal={selectedProposal}
          onClose={handleCloseModal}
          onVote={handleVote}
          voting={voting}
          votingPower={votingPower}
          showVoting={config.showVoting !== 'false'}
        />
      )}

      {/* Statistics Dashboard Modal */}
      {showStatistics && (
        <div className="t24-proposal-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowStatistics(false);
        }}>
          <div className="t24-proposal-modal">
            <StatisticsDashboard onClose={() => setShowStatistics(false)} />
          </div>
        </div>
      )}

      {/* Notification Preferences Modal */}
      {showNotifications && (
        <div className="t24-proposal-modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) setShowNotifications(false);
        }}>
          <div className="t24-proposal-modal">
            <NotificationPreferences onClose={() => setShowNotifications(false)} />
          </div>
        </div>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} />
    </div>
  );
}
